/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { traced } from "@workglow/util";
import type {
  DataPortSchemaObject,
  FromSchema,
  TypedArray,
  TypedArraySchemaOptions,
} from "@workglow/util/schema";
import type {
  AutoGeneratedKeys,
  InsertEntity,
  SimplifyPrimaryKey,
} from "../tabular/ITabularStorage";
import { TelemetryTabularStorage } from "../tabular/TelemetryTabularStorage";
import type { IVectorStorage, VectorSearchOptions } from "./IVectorStorage";

export class TelemetryVectorStorage<
  Metadata extends Record<string, unknown> | undefined,
  Schema extends DataPortSchemaObject,
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]> = ReadonlyArray<
    keyof Schema["properties"]
  >,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  InsertType = InsertEntity<Entity, AutoGeneratedKeys<Schema>>,
>
  extends TelemetryTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, InsertType>
  implements IVectorStorage<Metadata, Schema, Entity, PrimaryKeyNames, PrimaryKey, InsertType>
{
  private readonly vectorInner: IVectorStorage<
    Metadata,
    Schema,
    Entity,
    PrimaryKeyNames,
    PrimaryKey,
    InsertType
  >;

  constructor(
    storageName: string,
    inner: IVectorStorage<Metadata, Schema, Entity, PrimaryKeyNames, PrimaryKey, InsertType>
  ) {
    super(storageName, inner);
    this.vectorInner = inner;
  }

  getVectorDimensions(): number {
    return this.vectorInner.getVectorDimensions();
  }

  similaritySearch(
    query: TypedArray,
    options?: VectorSearchOptions<Metadata>
  ): Promise<(Entity & { score: number })[]> {
    return traced("workglow.storage.vector.similaritySearch", this.storageName, () =>
      this.vectorInner.similaritySearch(query, options)
    );
  }
}
